import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { hasAnalyticsConsent } from '@/lib/analytics/cookieConsent';

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

/**
 * Composant Google Analytics 4 avec consentement RGPD
 *
 * Ne charge GA4 que si :
 * 1. Le measurement ID est configuré
 * 2. L'utilisateur a donné son consentement
 */
export function GoogleAnalytics() {
  const hasConsent = hasAnalyticsConsent();

  useEffect(() => {
    // Écouter les changements de consentement
    const handleConsentChange = () => {
      // Recharger la page pour activer/désactiver GA4
      window.location.reload();
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);

    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
    };
  }, []);

  // Ne rien charger si :
  // - Pas de measurement ID configuré
  // - Pas de consentement utilisateur
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX' || !hasConsent) {
    return null;
  }

  return (
    <Helmet>
      {/* Google Analytics 4 */}
      <script
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
      />
      <script>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_MEASUREMENT_ID}', {
            anonymize_ip: true, // Anonymiser les IPs (RGPD)
            cookie_flags: 'SameSite=None;Secure', // Cookies sécurisés
          });
        `}
      </script>
    </Helmet>
  );
}

/**
 * Fonction helper pour tracker des événements personnalisés
 * Utiliser après avoir vérifié le consentement
 */
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, string | number | boolean>
) {
  if (!hasAnalyticsConsent()) {
    return; // Ne pas tracker sans consentement
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

/**
 * Tracker une page vue
 */
export function trackPageView(path: string, title: string) {
  if (!hasAnalyticsConsent()) {
    return;
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  }
}

// Déclarer gtag dans le type Window
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}
